import { useEffect, useMemo } from "react";
import log from "logbeacon";
import { ProfessionalIDEnum } from "../components";

import { ChatStore } from "../store";

const USER_DEEP_SEARCH_PREFERENCE_KEY = "user_deepSearch_preference";

export const updateIsDeepSearchForAgent = (agentId?: string) => {
  const isDeepSearchAgent =
    agentId === ProfessionalIDEnum.ANALYZER ||
    agentId === ProfessionalIDEnum.PIVOT;
  const getSavedPreference = (): boolean | null => {
    const saved = localStorage.getItem(USER_DEEP_SEARCH_PREFERENCE_KEY);
    if (saved === null) {
      return null;
    }
    try {
      return JSON.parse(saved);
    } catch {
      // Fallback for legacy string values
      return saved === "true";
    }
  };

  const setSavedPreference = (value: boolean): void => {
    localStorage.setItem(
      USER_DEEP_SEARCH_PREFERENCE_KEY,
      JSON.stringify(value),
    );
  };

  const removeSavedPreference = (): void => {
    localStorage.removeItem(USER_DEEP_SEARCH_PREFERENCE_KEY);
  };

  const savedPreference = getSavedPreference();

  log.debug("[updateIsDeepSearch] Triggered", {
    agentId,
    isDeepSearchAgent,
    currentChatStoreValue: ChatStore.isDeepsearch,
    savedUserPreference: savedPreference,
  });

  if (isDeepSearchAgent) {
    // Entering special agent - save user's current preference if not already saved
    if (savedPreference === null) {
      const currentPreference = ChatStore.isDeepsearch ?? false;
      setSavedPreference(currentPreference);
      // log.info('[useIsDeepSearchAgent] Saved user preference to localStorage:', currentPreference);
    } else {
      // log.info('[useIsDeepSearchAgent] User preference already saved:', savedPreference);
    }
    // Force enable for special agents
    // log.info('[useIsDeepSearchAgent] Forcing deep search to true for special agent');
    ChatStore.isDeepsearch = true;
  } else {
    // Leaving special agent or on normal pages
    if (savedPreference !== null) {
      // Restore user's original preference
      // log.info('[useIsDeepSearchAgent] Restoring user preference from localStorage:', savedPreference);
      ChatStore.isDeepsearch = savedPreference;
      removeSavedPreference();
    } else {
      // log.info('[useIsDeepSearchAgent] No saved preference, preserving current state:', ChatStore.isDeepsearch);
    }
    // If no saved preference, it means user never visited special agents,
    // so we preserve current state (don't modify ChatStore.isDeepsearch)
  }
};

/**
 * Hook to determine if the current agent supports deep search functionality
 * and automatically configure the deep search state accordingly.
 *
 * For supported agents (ANALYZER/PIVOT): Force enable deep search while on the page
 * For other cases: Restore user's original preference
 *
 * @param agentId - The ID of the current agent
 * @returns Object containing isDeepSearchAgent boolean
 */
export const useIsDeepSearchAgent = (agentId?: string) => {
  const isDeepSearchAgent = useMemo(() => {
    return (
      agentId === ProfessionalIDEnum.ANALYZER ||
      agentId === ProfessionalIDEnum.PIVOT
    );
  }, [agentId]);

  useEffect(() => {
    updateIsDeepSearchForAgent(agentId);
  }, [agentId, isDeepSearchAgent]);

  return { isDeepSearchAgent };
};
